import ICompany from "./company";
import Contract from "./contract";
import { IDocument } from "./document";
import { ISavRepairerQuote } from "./savRepairerQuote";

export const LIST_STATUS = [2, 3, 4, 5, 6, 7, 8, 9];

// Phase 3: Flags optionnels pour lecture prioritaire
interface AwaitingFlags {
  awaitingBeneficiary?: boolean;
  awaitingManager?: boolean;
  awaitingGarant?: boolean;
}

export function getCurrentStatusLabelForGestionnaireFromStatus(status?: number, flags?: AwaitingFlags): string {
  // Flags prioritaires
  if (flags?.awaitingBeneficiary) return 'En attente bénéficiaire';
  if (flags?.awaitingManager) return 'En attente gestionnaire';
  if (flags?.awaitingGarant) return 'En attente garant';

  // Fallback stateId
  switch (status) {
    case 1:
      return 'Temporaire';
    case 2:
      return 'Nouveau';
    case 3:
      return 'En attente bénéficiaire';
    case 4:
      return 'En attente gestionnaire';
    case 5:
      return 'Litige';
    case 6:
      return 'En attente garant';
    case 7:
      return 'A prendre en charge';
    case 8:
      return 'Pris en charge';
    case 9:
      return 'Sans suite';
    default:
      return 'Encours';
  }
}

export function getCurrentStatusLabelForGestionnaire(sav: ISav): string {
  return getCurrentStatusLabelForGestionnaireFromStatus(sav.status, {
    awaitingBeneficiary: sav.awaitingBeneficiary,
    awaitingManager: sav.awaitingManager,
    awaitingGarant: sav.awaitingGarant,
  });
}

export function getSavWithHelpers(sav: ISav): ISav & {
  getCurrentStatusLabelForGestionnaire: () => string;
  getDateDeclaration: () => Date;
  getDateCreation: () => Date;
  getDateModificationDeclarant: () => Date;
  getDateModificationOperateur: () => Date;
  getDateSinistre: () => Date;
} {
  return {
    ...sav,
    getCurrentStatusLabelForGestionnaire: () => getCurrentStatusLabelForGestionnaire(sav),
    getDateDeclaration: () => new Date(sav.dateDeclaration),
    getDateCreation: () => new Date(sav.dateCreation),
    getDateModificationDeclarant: () => new Date(sav.dateModificationDeclarant),
    getDateModificationOperateur: () => new Date(sav.dateModificationOperateur),
    getDateSinistre: () => new Date(sav.dateSinistre)
  };
}

export function getDecisionVoucherWithHelpers(decisionVoucher: IVoucher): IVoucher & {
  getCurrentStatusLabelForGestionnaire: () => string;
  getDateDeclaration: () => Date | undefined;
  getDateSentToSeller: () => Date | undefined;
  getDateSentToCustomer: () => Date | undefined;
  getDateSentToRepairer: () => Date | undefined;
  getDateSignedByCustomer: () => Date | undefined;
  getDateSignedBySeller: () => Date | undefined;

} {
  return {
    ...decisionVoucher,
    getCurrentStatusLabelForGestionnaire: () => getCurrentStatusLabelForGestionnaireFromStatus(2),
    getDateDeclaration: () => decisionVoucher.date ? new Date(decisionVoucher.date) : undefined,
    getDateSentToSeller: () => decisionVoucher.sentToSellerDateAt ? new Date(decisionVoucher.sentToSellerDateAt) : undefined,
    getDateSentToCustomer: () => decisionVoucher.sentToCustomerDateAt ? new Date(decisionVoucher.sentToCustomerDateAt) : undefined,
    getDateSentToRepairer: () => decisionVoucher.sentToRepairerDateAt ? new Date(decisionVoucher.sentToRepairerDateAt) : undefined,
    getDateSignedByCustomer: () => decisionVoucher.signedByCustomerDateAt ? new Date(decisionVoucher.signedByCustomerDateAt) : undefined,
    getDateSignedBySeller: () => decisionVoucher.signedBySellerDateAt ? new Date(decisionVoucher.signedBySellerDateAt) : undefined,
  };
}

export interface IVoucherAmountDetail {
  ref: string;
  label: string;
  quantity: number;
  unitPrice: number;
  amountTotalHT: number;
  amountTotalTTC: number;
  amountSeller: number;
  amountSellerCommercial: number;
}

export interface IVoucher {

  decision: 'partialOrTotalAgreement' | 'refusal' | 'commercialGesture';
  date?: string;
  comment: string;


  sentToSeller?: boolean;
  sentToSellerDateAt?: string;
  sentToSellerEmail?: string;

  sentToCustomer?: boolean;
  sentToCustomerDateAt?: string;
  sentToCustomerEmail?: string;

  sentToRepairer?: boolean;
  sentToRepairerDateAt?: string;
  sentToRepairerEmail?: string;

  signedByCustomer?: boolean;
  signedByCustomerDateAt?: string;

  signedBySeller?: boolean;
  signedBySellerDateAt?: string;

  amountSeller: number;
  amountSellerCommercial: number;
  amountDetails: Array<IVoucherAmountDetail>;

  partsIds?: number[];

  electonicSignatureDataFlow?: {
    payment_method?: string;
    seller: {
      addMessage?: string;
      overrideEmailTo?: string;
      documents?: number[];
      yousignSignerId?: string;
    };
    beneficiary: {
      addMessage?: string;
      overrideEmailTo?: string;
      documents?: number[];
      yousignSignerId?: string;
    };
    repairer: {
      addMessage?: string;
      overrideEmailTo?: string;
      documents?: number[];
    };
  };

  manualSignatureDataFlow?: {
    seller?: {
      addMessage?: string;
      overrideEmailTo?: string;
      documents?: number[];
    };
    beneficiary?: {
      addMessage?: string;
      overrideEmailTo?: string;
      documents?: number[];
    };
    repairer?: {
      addMessage?: string;
      overrideEmailTo?: string;
      documents?: number[];
    };
  }

  mailHtml?: string;
  mailSubject?: string;
  templateId?: string;
  templateVariables?: Record<string, any>;
}


export interface ITagRelationShip {
  savId: number;
  tagUUID: string;
  dateCreation: Date;

  tag: ITag;
}

export type TagConfigType = {
  color?: string;
};

export interface ITag {
  uuid: string;
  typeKey: string;
  tagKey: string;
  value: string;
  config: TagConfigType;
  dateCreation: Date;
  dateUpdate: Date;
}

export enum SavStatus {
  TEMPORAIRE = 1,
  NOUVEAU = 2,
  EN_ATTENTE_BENEFICIAIRE = 3,
  EN_ATTENTE_GESTIONNAIRE = 4,
  LITIGE = 5,
  EN_ATTENTE_GARANT = 6,
  A_PRENDRE_EN_CHARGE = 7,
  PRIS_EN_CHARGE = 8,
  SANS_SUITE = 9,
}

// Virtual Status Info (calculé par l'API, comme dans V2)
export interface VirtualStatusInfo {
  status: string;
  label: string;
  description?: string;
  phase?: string;
  actionRequired?: string;
  priority: number;
}

export default interface ISav {
  id: number;
  uniqueId?: string;
  isNew: boolean;
  status: SavStatus;
  idContract: number;
  contract: Contract;
  dateDeclaration: string;
  dateCreation: string;
  dateSinistre: string;
  dateModificationDeclarant: string;
  dateModificationDeclarantOperatorLastSeen: string;
  dateModificationOperateur: string;

  dateLastAskExpert: string;
  dateLastExpertResponse: string;

  amountBeneficiary: number;
  amountSeller: number;
  amountSellerCommercial: number;

  reparatorUuid: string | null;
  acceptedQuoteId: number | null;
  acceptedQuote: ISavRepairerQuote | null;

  savFormValues: IFormValue[];

  decisionVoucher?: IVoucher;
  decisionVoucherHistory?: Array<{
    date: string;
    decisionVoucher: IVoucher;
  }>;
  decisionVoucherDocId?: number | null;
  decisionVoucherDoc?: IDocument | null;

  savTagsRelationship: ITagRelationShip[];

  // Documents relations
  documents?: IDocument[];
  savDocumentsRelationship?: Array<{
    document: IDocument;
  }>;

  // ==================== MANAGER ASSIGNMENT ====================
  primaryManagerId?: string; // UUID du gestionnaire principal
  primaryManager?: {
    uuid: string;
    firstName: string;
    lastName: string;
    email: string;
  };

  // ==================== VIRTUAL STATUSES ====================
  // Virtual statuses (calculés par l'API via les stratégies)
  virtualStatuses?: VirtualStatusInfo[];

  // ==================== AWAITING FLAGS (Phase 3) ====================
  awaitingBeneficiary?: boolean;
  awaitingManager?: boolean;
  awaitingGarant?: boolean;

  // V2 specific fields
  version?: 1 | 2;
  currentInterventionId?: string;

  // Garant (tête de groupe si applicable, sinon contract.company)
  guarantorCompany?: ICompany;
}

export interface IFormValue {
  id: number;
  idSav: number;
  idSavForm: number;
  value: string;
  formField: IFormField;

  idFormField: number;

  valueDate: string;
  valueInt: number;
  valueBoolean: boolean;
  valueText: string;
  valueTextarea: string;
}

export interface IFormField {
  id: number;
  key: string;
  label: string;
  labelConsult: string;
  description: string;
  type: string;
}